// ./src/components/generic/RecordActions/RecordActions.tsx
import React from "react";
import { Group, Divider } from "@mantine/core";
import { AppButton } from "../AppButton";
import { OPERATION_RIGHTS } from "@/config";

// ============================================
// Types
// ============================================

interface WithRights {
  rights?: string[];
}

export interface RecordActionsProps<T = unknown> {
  /** Context object (usually the record) passed to all action functions */
  context: T;

  // Action handlers
  onViewClick?: (context: T) => void;
  onDuplicateClick?: (context: T) => void;
  onEditClick?: (context: T) => void;
  onRemoveClick?: (context: T) => void;
  onGoInClick?: (context: T) => void;

  // Loading states
  viewInProgress?: boolean;
  duplicateInProgress?: boolean;
  editInProgress?: boolean;
  removeInProgress?: boolean;
  goInInProgress?: boolean;

  // Disabled states
  viewDisabled?: boolean;
  duplicateDisabled?: boolean;
  editDisabled?: boolean;
  removeDisabled?: boolean;
  goInDisabled?: boolean;

  // Tooltips
  viewTooltip?: string;
  duplicateTooltip?: string;
  editTooltip?: string;
  removeTooltip?: string;
  goInTooltip?: string;

  /** Size of the action buttons */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Gap between action buttons */
  gap?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show dividers between action groups */
  withDividers?: boolean;
}

// ============================================
// Action Configuration
// ============================================

interface ActionConfig {
  key: string;
  icon: string;
  variant: "primary" | "secondary" | "tertiary" | "danger";
  group: "primary" | "destructive" | "navigation";
  right?: string;
}

const ACTION_CONFIG: Record<string, ActionConfig> = {
  view: {
    key: "view",
    icon: "Eye",
    variant: "tertiary",
    group: "primary",
  },
  duplicate: {
    key: "duplicate",
    icon: "Copy",
    variant: "tertiary",
    group: "primary",
    right: OPERATION_RIGHTS.DUPLICATE,
  },
  edit: {
    key: "edit",
    icon: "Edit",
    variant: "tertiary",
    group: "primary",
    right: OPERATION_RIGHTS.UPDATE,
  },
  remove: {
    key: "remove",
    icon: "Trash",
    variant: "danger",
    group: "destructive",
    right: OPERATION_RIGHTS.REMOVE,
  },
  goIn: {
    key: "goIn",
    icon: "ChevronRight",
    variant: "tertiary",
    group: "navigation",
  },
};

// ============================================
// Helpers
// ============================================

const hasRight = <T,>(context: T, right?: string): boolean => {
  if (!right) return true;
  const ctx = context as WithRights;
  if (!ctx || !Array.isArray(ctx.rights)) return true;
  return ctx.rights.includes(right);
};

// ============================================
// Component
// ============================================

export const RecordActions = <T,>({
  context,
  onViewClick,
  onDuplicateClick,
  onEditClick,
  onRemoveClick,
  onGoInClick,
  viewInProgress = false,
  duplicateInProgress = false,
  editInProgress = false,
  removeInProgress = false,
  goInInProgress = false,
  viewDisabled = false,
  duplicateDisabled = false,
  editDisabled = false,
  removeDisabled = false,
  goInDisabled = false,
  viewTooltip = "View",
  duplicateTooltip = "Duplicate",
  editTooltip = "Edit",
  removeTooltip = "Remove",
  goInTooltip = "Go to details",
  size = "xs",
  gap = "xs",
  withDividers = false,
}: RecordActionsProps<T>) => {
  // Build actions list based on provided handlers
  const actions = [
    {
      ...ACTION_CONFIG.view,
      handler: onViewClick,
      inProgress: viewInProgress,
      disabled: viewDisabled,
      tooltip: viewTooltip,
    },
    {
      ...ACTION_CONFIG.duplicate,
      handler: onDuplicateClick,
      inProgress: duplicateInProgress,
      disabled: duplicateDisabled,
      tooltip: duplicateTooltip,
    },
    {
      ...ACTION_CONFIG.edit,
      handler: onEditClick,
      inProgress: editInProgress,
      disabled: editDisabled,
      tooltip: editTooltip,
    },
    {
      ...ACTION_CONFIG.remove,
      handler: onRemoveClick,
      inProgress: removeInProgress,
      disabled: removeDisabled,
      tooltip: removeTooltip,
    },
    {
      ...ACTION_CONFIG.goIn,
      handler: onGoInClick,
      inProgress: goInInProgress,
      disabled: goInDisabled,
      tooltip: goInTooltip,
    },
  ].filter((action) => action.handler !== undefined);

  // Return null if no actions
  if (actions.length === 0) return null;

  // Render a single action button
  const renderAction = (action: (typeof actions)[0]) => {
    const isDisabledByRights = !hasRight(context, action.right);
    const isDisabled = action.disabled || isDisabledByRights;

    return (
      <AppButton
        key={action.key}
        variant={action.variant}
        iconName={action.icon}
        size={size}
        onClick={() => action.handler?.(context)}
        inProgress={action.inProgress}
        disabled={isDisabled}
        tooltip={action.tooltip}
      />
    );
  };

  // Simple render without dividers
  if (!withDividers) {
    return (
      <Group gap={gap} justify="flex-end" wrap="nowrap">
        {actions.map(renderAction)}
      </Group>
    );
  }

  // Group actions by type for dividers
  const groups = {
    primary: actions.filter((a) => a.group === "primary"),
    destructive: actions.filter((a) => a.group === "destructive"),
    navigation: actions.filter((a) => a.group === "navigation"),
  };

  const activeGroups = Object.values(groups).filter((g) => g.length > 0);

  return (
    <Group gap={gap} justify="flex-end" wrap="nowrap">
      {activeGroups.map((group, index) => (
        <React.Fragment key={group[0]?.key}>
          <Group gap={gap} wrap="nowrap">
            {group.map(renderAction)}
          </Group>
          {index < activeGroups.length - 1 && (
            <Divider orientation="vertical" color="neutral.2" />
          )}
        </React.Fragment>
      ))}
    </Group>
  );
};
